import { GenderOptions } from "app/constants/residentDetailsConstants";
import { useGetAllFloorsQuery } from "app/services/api/locationsApi";
import { useGetStatusFlagsIndexQuery } from "app/services/api/statusFlagsApi";
import { statusTypeOptions } from "app/services/api/types/residents/enums/ResidentStatusEnum";
import { useMemo, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { View } from "react-native";
import { FormControlledDropdown } from "ui/molecules/form/FormControlledDropdown";
import { ReusableModal } from "ui/molecules/modal/ReusableModal";
import { FormButtons } from "./FormButtons";
import { FilterButton } from "ui/atoms/button/FilterButton";
import { useResidentsFilterContext } from "../context/ResidentsFilterContext";
import { ResidentsFilterForm } from "../hooks/useResidentsFilterStorage";
import FormControlledLocationSelector from "app/components/molecules/FormControlledLocationSelector";
import {FormControlledMultiSelectDropdown} from "ui/molecules/form/FormControlledMultiSelectDropdown";
import { FormControlledDatePicker } from "ui/molecules/form/FormControlledDatePicker";

export function ResidentFilterComponent() {
  const [showMenu, setShowMenu] = useState(false);
  const { filters, updateFilters, clearAllFilters, hasFilters } =
    useResidentsFilterContext();

  const viewRef = useRef<View>(null);
  const { control, reset, handleSubmit, watch } = useForm<ResidentsFilterForm>({
    defaultValues: {
      location_id: filters.location_id,
      floor_id: filters.floor_id,
      status: filters.status,
      gender: filters.gender,
      status_flag_id: filters.status_flag_id,
      admission_start_date_from: filters.admission_start_date_from,
      admission_start_date_to: filters.admission_start_date_to,
      admission_end_date_from: filters.admission_end_date_from,
      admission_end_date_to: filters.admission_end_date_to,
    },
  });
  const locationId = watch("location_id");

  const { data: allFlags, isLoading: isAllFlagsLoading } =
    useGetStatusFlagsIndexQuery();

  const { data: floors = [], isLoading: isFloorsLoading } =
    useGetAllFloorsQuery({ locationId }, { skip: !locationId });

  const openMenu = () => {
    // Reset form with current filters when opening
    reset({
      location_id: filters.location_id,
      floor_id: filters.floor_id,
      status: filters.status,
      gender: filters.gender,
      status_flag_id: filters.status_flag_id,
      admission_start_date_from: filters.admission_start_date_from,
      admission_start_date_to: filters.admission_start_date_to,
      admission_end_date_from: filters.admission_end_date_from,
      admission_end_date_to: filters.admission_end_date_to,
    });
    setShowMenu(true);
  };

  const handleClose = () => {
    // Just close the modal without clearing filters
    setShowMenu(false);
  };

  const handleClearAll = async () => {
    // Clear all filters and close modal
    await clearAllFilters();
    reset({
      location_id: undefined,
      floor_id: undefined,
      status: undefined,
      gender: undefined,
      status_flag_id: undefined,
      admission_start_date_from: undefined,
      admission_start_date_to: undefined,
      admission_end_date_from: undefined,
      admission_end_date_to: undefined,
    });
    setShowMenu(false);
  };

  const onSubmit = async (data: ResidentsFilterForm) => {
    await updateFilters({
      location_id: data.location_id,
      floor_id: data.floor_id,
      status: data.status,
      gender: data.gender,
      status_flag_id: data.status_flag_id,
      admission_start_date_from: data.admission_start_date_from,
      admission_start_date_to: data.admission_start_date_to,
      admission_end_date_from: data.admission_end_date_from,
      admission_end_date_to: data.admission_end_date_to,
    });
    setShowMenu(false);
  };

  const floorsMapping = useMemo(
    () =>
      floors.map((floor) => ({
        value: floor.id,
        label: floor.name,
      })),
    [floors],
  );
  const flagMapping = useMemo(() => {
    return (
      allFlags?.map((flag) => ({
        value: flag.id,
        label: flag.name,
      })) ?? []
    );
  }, [allFlags]);

  return (
    <>
      <View ref={viewRef}>
        <FilterButton onPress={openMenu} hasFilters={hasFilters} />
      </View>

      <ReusableModal
        title="Filter Residents"
        isVisible={showMenu}
        onClose={handleClose}
        className="w-full max-w-[600px] mx-auto"
      >
        <View className="grid md:grid-cols-2 gap-4">
          <FormControlledLocationSelector<ResidentsFilterForm>
            name="location_id"
            control={control}
            hideSearch
            canClear
            label="Location"
          />
          <FormControlledMultiSelectDropdown<ResidentsFilterForm>
            name="floor_id"
            control={control}
            placeholder="Choose floors"
            isDisabled={isFloorsLoading || !locationId}
            options={floorsMapping}
            label="Floor"
            canClear
          />
          <FormControlledDropdown<ResidentsFilterForm>
            name="gender"
            control={control}
            placeholder="Choose gender"
            options={GenderOptions}
            label="Gender"
            hideSearch
            canClear
          />
          <FormControlledMultiSelectDropdown<ResidentsFilterForm>
            name="status"
            control={control}
            placeholder="Choose status"
            options={statusTypeOptions}
            label="Status"
            hideSearch
            canClear
          />
          <FormControlledDropdown<ResidentsFilterForm>
            name="status_flag_id"
            control={control}
            placeholder="Choose status flags"
            isDisabled={isAllFlagsLoading}
            options={flagMapping}
            label="Status Flags"
            canClear
          />
        </View>
        <View className="grid md:grid-cols-2 gap-4 mt-4">
          <FormControlledDatePicker<ResidentsFilterForm>
            id="admission_start_date_from"
            name="admission_start_date_from"
            control={control}
            label="Check In From"
            placeholder="Select start date"
          />
          <FormControlledDatePicker<ResidentsFilterForm>
            id="admission_start_date_to"
            name="admission_start_date_to"
            control={control}
            label="Check In To"
            placeholder="Select end date"
          />
          <FormControlledDatePicker<ResidentsFilterForm>
            id="admission_end_date_from"
            name="admission_end_date_from"
            control={control}
            label="Check Out From"
            placeholder="Select start date"
          />
          <FormControlledDatePicker<ResidentsFilterForm>
            id="admission_end_date_to"
            name="admission_end_date_to"
            control={control}
            label="Check Out To"
            placeholder="Select end date"
          />
        </View>
        <FormButtons
          okTitle="Apply"
          cancelTitle="Clear All"
          onClose={handleClearAll}
          onSave={handleSubmit(onSubmit)}
        />
      </ReusableModal>
    </>
  );
}
